import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MessagesSquare, MailPlus, Archive, ArchiveRestore, Trash2, Search } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { MessageThread } from "../../components/MessageThread";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";

const STATUS_STYLES = {
  open: "border-emerald-200 bg-emerald-50 text-emerald-700",
  waiting: "border-amber-200 bg-amber-50 text-amber-700",
  resolved: "border-neutral-200 bg-neutral-100 text-neutral-500",
};

export default function Conversations() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [detail, setDetail] = useState(null);
  const [sending, setSending] = useState(false);
  const [canned, setCanned] = useState([]);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [showSend, setShowSend] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sendForm, setSendForm] = useState({ subject: "", message: "", include_files: true });
  const [sendingDoc, setSendingDoc] = useState(false);

  const loadList = useCallback(() => {
    const qs = tab === "archived" ? "?archived=true" : "";
    api.get(`/business/conversations${qs}`).then(({ data }) => setConvs(data)).catch((e) => toast.error(apiError(e)));
  }, [tab]);

  const loadDetail = useCallback(() => {
    if (!id) {
      setDetail(null);
      return;
    }
    api.get(`/business/conversations/${id}`)
      .then(({ data }) => {
        setDetail(data);
        loadList();
      })
      .catch((e) => toast.error(apiError(e)));
  }, [id, loadList]);

  useEffect(() => {
    loadList();
  }, [loadList]);
  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    api.get("/business/canned-replies").then(({ data }) => setCanned(data)).catch(() => {});
  }, []);

  const setStatus = async (status) => {
    try {
      await api.patch(`/business/conversations/${id}/status`, { status });
      loadDetail();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleArchive = async () => {
    try {
      const archived = !detail?.conversation?.archived;
      await api.patch(`/business/conversations/${id}/archive`, { archived });
      toast.success(archived ? "Conversation archived" : "Conversation restored");
      setDetail(null);
      navigate("/dashboard/conversations");
      loadList();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteConv = async () => {
    try {
      await api.delete(`/business/conversations/${id}`);
      toast.success("Conversation deleted");
      setConfirmDelete(false);
      setDetail(null);
      navigate("/dashboard/conversations");
      loadList();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const businessTools = {
    allowInternal: true,
    cannedReplies: canned,
    onSuggest: async () => {
      try {
        const { data } = await api.post(`/business/conversations/${id}/suggest`);
        return data.draft;
      } catch (e) {
        toast.error(apiError(e));
        return "";
      }
    },
    onFileRequest: async (text) => {
      try {
        await api.post(`/business/conversations/${id}/file-requests`, {
          body: text || "Please upload the requested files.",
        });
        toast.success("File request sent");
        loadDetail();
      } catch (e) {
        toast.error(apiError(e));
      }
    },
  };

  const sendToCustomer = async () => {
    if (!sendForm.subject.trim() || !sendForm.message.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setSendingDoc(true);
    try {
      const { data } = await api.post(`/business/conversations/${id}/send-to-customer`, sendForm);
      toast.success(data.status === "sent" ? "Email sent to customer" : `Email logged (status: ${data.status}) — confirmation link active`);
      setShowSend(false);
      setSendForm({ subject: "", message: "", include_files: true });
      loadDetail();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSendingDoc(false);
    }
  };

  const send = async (body, files, opts = {}) => {
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("body", body);
      fd.append("internal", opts.internal ? "true" : "false");
      files.forEach((f) => fd.append("files", f));
      await api.post(`/business/conversations/${id}/messages`, fd);
      loadDetail();
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="h-[calc(100vh-6rem)]" data-testid="conversations-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <h1 className="text-3xl font-black">Conversations</h1>
        <div className="flex gap-1 bg-white border border-[#E5E5E5] rounded-full p-1" data-testid="conversation-filters">
          {[["all", "All"], ["open", "Open"], ["waiting", "Waiting"], ["resolved", "Resolved"], ["archived", "Archived"]].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`filter-${k}`}
              className={`text-xs px-3.5 py-1.5 rounded-full transition-colors duration-150 ${tab === k ? "bg-neutral-900 text-white" : "text-neutral-500 hover:text-neutral-900"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="grid lg:grid-cols-[340px_1fr] gap-4 h-[calc(100%-4.5rem)]">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-y-auto" data-testid="conversation-list">
          <div className="p-3 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
            <div className="relative">
              <Search className="h-4 w-4 text-neutral-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, form…"
                className="pl-9 h-10"
                data-testid="conversation-search-input"
              />
            </div>
          </div>
          {convs.length === 0 && <p className="p-6 text-sm text-neutral-400" data-testid="conversations-empty">No conversations yet.</p>}
          {convs
            .filter((c) => tab === "all" || tab === "archived" || (c.status || "open") === tab)
            .filter((c) => {
              if (!query.trim()) return true;
              const q = query.toLowerCase();
              return (c.customer_name || "").toLowerCase().includes(q) || (c.customer_email || "").toLowerCase().includes(q) || (c.form_title || "").toLowerCase().includes(q);
            })
            .map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/dashboard/conversations/${c.id}`)}
              data-testid={`conversation-item-${c.id}`}
              className={`w-full text-left p-4 border-b border-[#E5E5E5] transition-colors duration-150 ${
                id === c.id ? "bg-neutral-50" : "hover:bg-neutral-50"
              }`}
            >              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-sm truncate">{c.customer_name || c.customer_email}</span>
                {c.unread > 0 && (
                  <span className="text-[10px] bg-neutral-900 text-white rounded-full px-2 py-0.5 shrink-0" data-testid={`conversation-unread-${c.id}`}>
                    {c.unread}
                  </span>
                )}
              </div>
              <div className="text-xs text-neutral-400 mt-0.5 flex items-center gap-2 flex-wrap">
                {c.form_title}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${STATUS_STYLES[c.status || "open"]}`} data-testid={`conversation-status-${c.id}`}>{c.status || "open"}</span>
                {c.channel === "widget" && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-sky-200 bg-sky-50 text-sky-700">widget</span>}
                {c.channel === "assisted" && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-violet-200 bg-violet-50 text-violet-700">assisted</span>}
                {c.pending_verification && <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700" data-testid={`conversation-unverified-${c.id}`}>unverified</span>}
              </div>
              {c.last_message && (
                <p className="text-xs text-neutral-500 mt-1.5 truncate">{c.last_message.body || "Attachment"}</p>
              )}
            </button>
          ))}
        </div>

        <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden flex flex-col" data-testid="conversation-detail">
          {detail ? (
            <>
              <div className="px-5 py-4 border-b border-[#E5E5E5] shrink-0 flex items-center justify-between gap-4">
                <div>
                  <div className="font-bold" data-testid="conversation-customer-name">
                    {detail.conversation.customer_name || detail.conversation.customer_email}
                  </div>
                  <div className="text-xs text-neutral-400">
                    {detail.conversation.customer_email} · {detail.conversation.form_title}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
                  {detail.conversation.archived && (
                    <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-neutral-300 bg-neutral-100 text-neutral-500" data-testid="conversation-archived-badge">
                      Archived
                    </span>
                  )}
                  <Button variant="outline" size="sm" className="rounded-full" onClick={() => setShowSend(true)} data-testid="send-to-customer-button">
                    <MailPlus className="h-3.5 w-3.5 mr-1.5" /> Send to customer
                  </Button>
                  <Select value={detail.conversation.status || "open"} onValueChange={setStatus} disabled={!!detail.conversation.archived}>
                    <SelectTrigger className="w-32 h-9 rounded-full" data-testid="conversation-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="open" data-testid="status-option-open">Open</SelectItem>
                      <SelectItem value="waiting" data-testid="status-option-waiting">Waiting</SelectItem>
                      <SelectItem value="resolved" data-testid="status-option-resolved">Resolved</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-full"
                    onClick={toggleArchive}
                    data-testid={detail.conversation.archived ? "conversation-restore-button" : "conversation-archive-button"}
                  >
                    {detail.conversation.archived ? (
                      <><ArchiveRestore className="h-3.5 w-3.5 mr-1" /> Restore</>
                    ) : (
                      <><Archive className="h-3.5 w-3.5 mr-1" /> Archive</>
                    )}
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setConfirmDelete(true)} aria-label="Delete conversation" title="Delete conversation" data-testid="conversation-delete-button">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex-1 min-h-0">
                <MessageThread
                  messages={detail.messages}
                  selfSender="business"
                  onSend={send}
                  sending={sending}
                  testPrefix="business-thread"
                  businessTools={businessTools}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-neutral-400" data-testid="conversation-placeholder">
              <div className="text-center">
                <MessagesSquare className="h-10 w-10 mx-auto text-neutral-200" />
                <p className="mt-3 text-sm">Select a conversation to view the thread.</p>
              </div>
            </div>
          )}
        </div>
      </div>
      <Dialog open={showSend} onOpenChange={setShowSend}>
        <DialogContent className="bg-white max-w-md" data-testid="send-to-customer-dialog">
          <DialogHeader>
            <DialogTitle>Send to customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-neutral-500">
              Emails the customer with a tracked confirmation link — the customer must confirm they have read it. Logged in this thread.
            </p>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input value={sendForm.subject} onChange={(e) => setSendForm({ ...sendForm, subject: e.target.value })} placeholder="Your documents" data-testid="send-subject-input" />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea rows={4} value={sendForm.message} onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })} placeholder="Please find your documents attached…" data-testid="send-message-input" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={sendForm.include_files} onCheckedChange={(v) => setSendForm({ ...sendForm, include_files: v })} data-testid="send-include-files-switch" />
              <Label>Include submission files as attachments</Label>
            </div>
            <Button onClick={sendToCustomer} disabled={sendingDoc} className="w-full rounded-full" data-testid="send-to-customer-submit">
              {sendingDoc ? "Sending…" : "Send with confirmation tracking"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="bg-white max-w-sm" data-testid="delete-conversation-dialog">
          <DialogHeader>
            <DialogTitle>Delete this conversation?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-500">
            All messages and history with <b>{detail?.conversation?.customer_name || detail?.conversation?.customer_email}</b> will be permanently removed. This cannot be undone.
          </p>
          <div className="flex gap-3 justify-end pt-2">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmDelete(false)} data-testid="delete-conversation-cancel">
              Cancel
            </Button>
            <Button className="rounded-full bg-red-600 hover:bg-red-700" onClick={deleteConv} data-testid="delete-conversation-confirm">
              Delete permanently
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
